import { useController } from "react-hook-form";

import DatePickerInput from "@/form/inputs/DatePickerInput/DatePickerInput";
import { useFieldIds } from "@/form/useFieldIds";
import { cn } from "@/lib/utils";

import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

export type DatePickerFieldProps = {
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly label: string;
  readonly labelClassName?: string;
  readonly hint?: string;
  readonly placeholder?: string;
  readonly name: string;
  readonly "data-testid"?: string;
  readonly onBlur?: (date: Date | undefined) => void;
  readonly onChange?: (date: Date | undefined) => void;
  readonly hideLabel?: boolean;
  readonly hideValidation?: boolean;
  readonly minDate?: Date;
  readonly maxDate?: Date;
  readonly errorClassname?: string;
};

export default function DatePickerField({
  disabled = false,
  label,
  name,
  hint,
  placeholder,
  onBlur,
  onChange,
  hideLabel = false,
  hideValidation = false,
  minDate,
  maxDate,
  errorClassname,
  readOnly = false,
}: DatePickerFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId, describedBy } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
  });

  const invalid = !!fieldState.error;

  const handleChange = (date: Date | undefined) => {
    onChange?.(date);
    field.onChange(date);
  };

  const handleBlur = () => {
    onBlur?.(field.value);
    field.onBlur();
  };

  return (
    <div
      className={cn("DatePickerField grid flex-1 gap-2")}
      data-testid="DatePickerField"
      data-field={name}
    >
      <div className={cn("grid", hideLabel && "sr-only")}>
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      </div>

      <DatePickerInput
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
        aria-invalid={invalid}
        disabled={disabled}
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        value={field.value}
        minDate={minDate}
        maxDate={maxDate}
        onBlur={handleBlur}
        onChange={handleChange}
      />

      <div
        className={cn(
          "align-center flex h-3 justify-start",
          hideValidation && "hidden",
          errorClassname
        )}
      >
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
